// Import react elements
import React, { useContext, useMemo, useState } from "react";

// Import context
import { UserContext } from "global/context/UserContext";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";
import useImageUploader from "global/hooks/useImageUploader";
import { useCustomizr } from "global/hooks/useCustomizr";


// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";
import { ImageLoader } from "global/components/ImageLoader";

import  RenderAddNewSurfacePopup  from "./AddNewSurfacePopUp";
import  RenderAddNewPrintAreaPopup  from "./AddNewPrintAreaPopUp";
import  RenderSurfaceDetails  from "./SurfaceDetails";
import  RenderPrintAreaDetails  from "./PrintAreaDetails";
import  RenderPositionDetails  from "./PositionDetails";
import  RenderMockupPopup  from "./MockupDetails";
import  RenderPrintLayoutPopup  from "./PrintLayoutDetails";
import  RenderViewPopup  from "./ViewDetails";
import  ToggleSwitch from "global/components/ToggleSwitch";
import  RenderColourwayDetails  from "./ColourwayDetails";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";
import RenderImageUploader from "global/components/ImageUploader";
import { InfoButton } from "global/components/InfoButton";



// Table / Grid view of all custom products
const CustomProduct = ({
        selectedCustomProduct, 
        setSelectedCustomProduct,
        addingNewCustomProduct,
        selectedSurfaceData,
        setSelectedSurfaceData,
        selectedPrintAreaData,
        setSelectedPrintAreaData,
        customProductsMMMLinks,
        onCloseEditor
}) => {


    // Get connections data
    const { userData } = useContext(UserContext);

    // Define toast options
    const { toastError } = useToast();

    // State for loading when saving
    const [loading, setLoading] = useState(false);

    // Store the product-level section selected in the editor tree
    const [activeSection, setActiveSection] = useState("product");

    // Track unsaved changes inside the currently mounted surface or print area editor
    const [nestedEditorDirty, setNestedEditorDirty] = useState(false);

    // State for selected print area tab index
    const [activePrintAreaTab, setActivePrintAreaTab] = useState(0);

    // Open close state for add new SURFACES pop up
    const [addNewSurfacePopupIsOpen, setAddNewSurfacePopupIsOpen] = useState(false);
    
    // Open close state for add new PRINT AREA pop up
    const [addNewPrintAreaPopupIsOpen, setAddNewPrintAreaPopupIsOpen] = useState(false);

    // Store the print area currently being dragged for ordering changes
    const [draggedPrintAreaCode, setDraggedPrintAreaCode] = useState(null);

    // Store the print area currently being dragged over
    const [dragOverPrintAreaCode, setDragOverPrintAreaCode] = useState(null);

    // Prevent another ordering operation while the current one is being saved
    const [reorderingPrintAreas, setReorderingPrintAreas] = useState(false);

    // Open close state for adding / editing a POSITION pop up
    const [addEditPositionPopupIsOpen, setAddEditPositionPopupIsOpen] = useState(false);

    // State for storing selected position
    const [selectedPosition, setSelectedPosition] = useState(null);

    // Open close state for VIEW pop up - to view large image and delete but can't edit
    const [viewPopupIsOpen, setViewPopupIsOpen] = useState(false);

    // State for storing selected VIEW
    const [selectedView, setSelectedView] = useState(null);

    // State for storing print area roles
    const [printAreaRoles, setPrintAreaRoles] = useState(null);

    // Open close state for MOCKUP pop up - to view large image and delete but can't edit
    const [mockupPopupIsOpen, setMockupPopupIsOpen] = useState(false);

    // State for storing selected MOCKUP
    const [selectedMockup, setSelectedMockup] = useState(null);

    // Open close state for PRINT LAYOUT pop up - to view large image and delete but can't edit
    const [printLayoutPopupIsOpen, setPrintLayoutPopupIsOpen] = useState(false);

    // State for storing selected PRINT LAYOUT
    const [selectedPrintLayout, setSelectedPrintLayout] = useState(null);

 

    // Get the Customizr open function and ready flag.
    const { openCustomizr, isReady: isCustomizrReady } = useCustomizr();

    // Wait a short time before trying the refresh again.
    const wait = (ms) => new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

    // Build a simple snapshot so we can tell when Customizr changes the data.
    const getCustomizrRefreshSignature = (tokenType, productData, surfaceData, printAreaData) => {
        switch (tokenType) {
            case "view":
                return JSON.stringify((printAreaData?.views || []).map((view) => view.style + "::" + view.name));

            case "printlayout":
                return JSON.stringify(productData?.print_layouts || []);

            case "mockup":
                return JSON.stringify(productData?.mockups || []);

            default:
                return JSON.stringify({
                    product: productData?.code || "",
                    surface: surfaceData?.details?.code || "",
                    printArea: printAreaData?.code || "",
                });
        }
    };

    // Reload the product data until the new Customizr change appears.
    const refreshCustomProductSelections = async (tokenType) => {
        // Save what the data looks like before we open Customizr.
        const previousSignature = getCustomizrRefreshSignature(
            tokenType,
            selectedCustomProduct,
            selectedSurfaceData,
            selectedPrintAreaData
        );

        // Keep the newest fetch result we get back.
        let latestResponse = null;

        // Try a few times because the save can take a moment to appear.
        for (let attempt = 0; attempt < 5; attempt += 1) {
            // Reload the current product, surface, and print area.
            const fetchResponse = await fetchCustomProducts(
                selectedCustomProduct?.code,
                selectedSurfaceData,
                selectedPrintAreaData?.code
            );

            // Keep the latest good result so we can use it later.
            if (fetchResponse?.selectedProduct) {
                latestResponse = fetchResponse;
            }

            // Build a new snapshot from the latest data.
            const nextSignature = getCustomizrRefreshSignature(
                tokenType,
                fetchResponse?.selectedProduct,
                fetchResponse?.selectedSurface,
                fetchResponse?.selectedPrintArea
            );

            // Stop retrying once the data looks different.
            if (nextSignature !== previousSignature) {
                latestResponse = fetchResponse;
                break;
            }

            // Wait a moment before trying again.
            await wait(500);
        }

        // Stop here if we never got a good product back.
        if (!latestResponse?.selectedProduct) {
            return;
        }

        // Update the page with the newest product data.
        setSelectedCustomProduct(latestResponse.selectedProduct || null);
        // Update the selected surface if we got one back.
        setSelectedSurfaceData(latestResponse.selectedSurface ?? selectedSurfaceData ?? null);
        // Update the selected print area if we got one back.
        setSelectedPrintAreaData(latestResponse.selectedPrintArea ?? selectedPrintAreaData ?? null);
    };


    // Custom hook for fetching and managing product data
    const {
        fetchCustomProducts,
        addNewCustomProduct,
        updateExistingCustomProduct,
        removeCustomProduct,
        addNewCustomProductColourway,
        updateExistingColourway,
        deleteColourway,
        addNewCustomProductSurface,
        updateExistingSurface,
        addNewCustomProductPrintArea,
        updateExistingPrintArea,
        reorderPrintAreas,
        addUpdatePosition,
        updateView,
        deleteView,
        getMockupCloneTargets,
        cloneMockup,
        updateMockup,
        deleteMockup,
        deletePrintLayout
        
    } = useCustomProductsData(); 


    // Hook for storing the selected image details
    const {
        imageDetails,
        setImageDetails
    } = useImageUploader(); 

    




  // -------------------------------
  // useFormHandler
  // -------------------------------

    // Memoize the initial form values to avoid recalculating unless details change
    const initialFormValues = useMemo(() => {

        if (selectedCustomProduct) {

            // Return the initial form values
            return {

                // Spread all existing connection details into the form state
                ...selectedCustomProduct,
                //active: S2B(itemDetails.active)

                surfaces: selectedCustomProduct.surfaces || [],
                image_data: "",

            };

        }

    // Dependency array to recompute initialFormValues only when details change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomProduct]);



    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        //setFormValues,
        handleChange,   // Function to update form values when inputs change
        resetForm,      // Function to reset data on the form
        isFormPartlyEdited, // Check to see if form has been partially edited
        //fieldErrors,    // Object storing validation errors for individual fields
        //formError,      // General error message (e.g. API errors)
        
       
    } = useFormHandler({

        // Default values for the form fields
        initialValues: initialFormValues,

        // Enable syncing - because we're using memo to remember the initial form values before the user edits them
        syncInitialValues: true,

        // API function to be called on form submission based on adding a new product or updating an existing one
        //apiCall: addUpdateCustomProduct,

        // We want to convert boolean to string
        convertBooleans: true,

        // We want to convert number strings to numbers
        convertNumbers: true,

        // Validation function to check the form fields before submission
        validate: (values) => {

            // Set errors to empty
            const errors = {};

            // Validation rules

            // Return validation errors (if any)
            return errors;

        },

    });

    // Close the custom product popup and clear its nested selections
    const closeEditor = () => {

        // Reset the local product form
        resetForm();

        // Clear nested selections before the popup closes
        setSelectedSurfaceData(null);
        setSelectedPrintAreaData(null);
        setNestedEditorDirty(false);

        // Let the page close the popup and refresh the product list underneath it
        onCloseEditor();

    };


    // Close the product editor, warning first when any visible editor has unsaved changes
    const handleClose = () => {

        // Check the product form and the currently mounted nested editor
        if (isFormPartlyEdited() || nestedEditorDirty) {

            DisplayConfirmationDialog(
                "You have unsaved changes. Are you sure you want to close?",
                "Close",
                "Keep Editing",
                async function (is_confirmed) {
                    if (is_confirmed) { closeEditor(); }
                }
            );

            return;

        }

        // Nothing is waiting to be saved so close immediately
        closeEditor();

    };


    // Apply a tree navigation change after any discard warning has been dealt with
    const applyEditorNavigation = (section, surfaceData = null, printAreaData = null) => {

        // Store the requested product-level section
        setActiveSection(section);

        // Store the nested selections for the Surfaces branch
        setSelectedSurfaceData(surfaceData);
        setSelectedPrintAreaData(printAreaData);

        // Reset the print area tabs whenever a different node is selected
        setActivePrintAreaTab(0);

        // The old nested editor is being discarded or replaced
        setNestedEditorDirty(false);

    };


    // Navigate around the editor tree without silently discarding nested form changes
    const handleEditorNavigation = (section, surfaceData = null, printAreaData = null) => {

        // Surface and print area forms are local to their editor components and would unmount
        if (nestedEditorDirty) {

            DisplayConfirmationDialog(
                "You have unsaved changes. Are you sure you want to leave this section?",
                "Leave",
                "Keep Editing",
                async function (is_confirmed) {
                    if (is_confirmed) { applyEditorNavigation(section, surfaceData, printAreaData); }
                }
            );

            return;

        }

        // Nothing in the current nested editor would be lost
        applyEditorNavigation(section, surfaceData, printAreaData);

    };


    //console.log("formValues: ", formValues);

    // Function to handle save button click
    const handleSave = async () => {

        // Set a loading state?
        setLoading(true);

        // Check if we're adding a new custom product
        if (addingNewCustomProduct) {

            // We're adding a new custom product

            // Make the call
            const response = await addNewCustomProduct(formValues);

            // If successful
            if (response?.success) {

                // Clear loading before closing the editor
                setLoading(false);

                // Close the popup and refresh the list underneath it
                closeEditor();
            

            } else {

                // Toast fail message
                toastError({ title: "Failed", message: "Could not add new custom product: " + response.error_message, autoCloseMs: 6000 });

                // Clear loading
                setLoading(false);

            }

        } else {

            // We're updating an existing custom product

            // Remove the image_data field so it doesn't clear the custom product image
            let customProductData;

            if (formValues?.image_data !== (null || "")) {

                customProductData = formValues;

            } else {

                const { image_data, ...formValuesWithoutImageData } = formValues;

                customProductData = formValuesWithoutImageData;

            }
            
            // Make the call
            const response = await updateExistingCustomProduct(selectedCustomProduct.code, customProductData);

            // If successful
            if (response?.success) {

                // Update selected product so form initial values are no longer stale
                setSelectedCustomProduct((prev) => ({

                    // Preserve existing nested data
                    ...prev,

                    // Apply latest saved form values
                    ...formValues,

                }));

                // Clear loading
                setLoading(false);

            } else {

                // Clear loading
                setLoading(false);

                // Toast fail message
                toastError({ title: "Failed", message: "Could not update custom product: " + response.error_message, autoCloseMs: 6000 });

            }   

        }

    };



    // Function to handle delete button click
    const handleDelete = async (customProductCode) => {

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this custom product: " + formValues.name + "?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {
            
                // Return early if they cancel
                if (is_confirmed) {

                    // Set loading
                    setLoading(true);

                    // Make the call
                    const response = await removeCustomProduct(customProductCode);

                    // If successful
                    if (response?.success) {
                        
                        // Clear loading before closing the editor
                        setLoading(false);

                        // Close the popup and refresh the list underneath it
                        closeEditor();


                    } else {

                        // Clear loading
                        setLoading(false);

                        // Toast fail message
                        toastError({ title: "Error", message: "Could not delete custom product: " + response.error_message, autoCloseMs: 6000 });

                    }
                        
                };

            }
            
        );

    };







    // **** SURFACES **** //

    // Function to handle saving a new surface being added
    const handleSaveNewSurface = async (surfaceData) => {
        
        // Make the call
        const response = await addNewCustomProductSurface(surfaceData, selectedCustomProduct.code, setSelectedCustomProduct);

        // If successful
        if (response?.success) {
        
            // Close the pop up
            setAddNewSurfacePopupIsOpen(false);

        } else {

            // Toast failure message
            toastError({ title: "Error", message: "Could not add surface: " + response.error_message });

            return;

        }
        
    }
    


    // Render the print areas belonging to the currently selected surface
    const renderSurfacePrintAreas = () => {

        return (

            <div className="surfaces-container">

                {[...(selectedSurfaceData?.details.print_areas || [])]
                    .sort((firstPrintArea, secondPrintArea) => Number(firstPrintArea.ordering) - Number(secondPrintArea.ordering))
                    .map((printArea) => (

                    <div
                        key={printArea.code}
                        className={"surface-container surface-container-with-text surface-container--draggable" + (dragOverPrintAreaCode === printArea.code ? " surface-container--drag-over" : "")}
                        draggable={!reorderingPrintAreas}
                        title="Drag to reorder or click to edit"
                        onClick={() => handleEditorNavigation("surfaces", selectedSurfaceData, printArea)}
                        onDragStart={(event) => handlePrintAreaDragStart(printArea, event)}
                        onDragOver={(event) => handlePrintAreaDragOver(printArea, event)}
                        onDragLeave={handlePrintAreaDragLeave}
                        onDrop={(event) => handlePrintAreaDrop(printArea, event)}
                        onDragEnd={handlePrintAreaDragEnd}
                    >

                        <div className="surface-details">
                            <p><span className="surfaces-title">{printArea.name}</span></p>
                            <p>{printArea.code}</p>
                            <p><span className="surfaces-description">{printArea.width} x {printArea.height}</span></p>
                        </div>

                    </div>

                ))}

                <div className="surface-container__add-new surface-container-with-text">

                    {addingNewCustomProduct

                        ? <p>You need to save the product before adding print areas.</p>

                        : <CreateButton title="+ Add Print Area" size="s" onClick={() => {setAddNewPrintAreaPopupIsOpen(true)}} />
                    }

                </div>

                {addNewPrintAreaPopupIsOpen &&

                    <RenderAddNewPrintAreaPopup
                        isOpen={addNewPrintAreaPopupIsOpen}
                        onClose={() => {
                            setAddNewPrintAreaPopupIsOpen(false);
                        }}
                        handleSaveNewPrintArea={handleSaveNewPrintArea}
                        printAreaRoles={printAreaRoles}
                        setPrintAreaRoles={setPrintAreaRoles}
                    />

                }

            </div>

        );

    };




    // **** Print Areas **** //

    // Function to handle saving a new surface being added
    const handleSaveNewPrintArea = async (printAreaData) => {
        
        // Make the call
        const response = await addNewCustomProductPrintArea(printAreaData, selectedCustomProduct.code, selectedSurfaceData, setSelectedCustomProduct, setSelectedSurfaceData);

        // If successful
        if (response?.success) {
        
            // Close the pop up
            setAddNewPrintAreaPopupIsOpen(false);

        } else {

            // Toast failure message
            toastError({ title: "Error", message: "Could not add print area: " + response.error_message });

            return;

        }
        
    }


    // Start dragging a print area for ordering
    const handlePrintAreaDragStart = (printArea, event) => {

        // Prevent another drag while an ordering update is being saved
        if (reorderingPrintAreas) {
            event.preventDefault();
            return;
        }

        // Store the dragged print area code
        setDraggedPrintAreaCode(printArea.code);

        // Store the code on the native drag event as well
        event.dataTransfer.setData("text/plain", printArea.code);

        // Tell the browser this is a move operation
        event.dataTransfer.effectAllowed = "move";

    };


    // Track which print area the dragged card is currently over
    const handlePrintAreaDragOver = (printArea, event) => {

        // Allow this element to receive the drop
        event.preventDefault();

        // Do not highlight the card being dragged
        if (draggedPrintAreaCode === printArea.code) {
            setDragOverPrintAreaCode(null);
            return;
        }

        // Store the current drop target
        setDragOverPrintAreaCode(printArea.code);

    };


    // Clear the current print area drop target
    const handlePrintAreaDragLeave = () => {

        // Clear the drop highlight
        setDragOverPrintAreaCode(null);

    };


    // Drop one print area onto another and save the resulting complete order
    const handlePrintAreaDrop = async (targetPrintArea, event) => {

        // Prevent the browser's normal drop handling
        event.preventDefault();

        // Stop another ordering operation starting while one is already saving
        if (reorderingPrintAreas) {
            return;
        }

        // Read the dragged print area code from state first
        let droppedPrintAreaCode = draggedPrintAreaCode;

        // Use the native drag data if state is unavailable
        if (!droppedPrintAreaCode) {
            droppedPrintAreaCode = event.dataTransfer.getData("text/plain");
        }

        // Clear the drag state
        setDraggedPrintAreaCode(null);
        setDragOverPrintAreaCode(null);

        // Stop if no dragged print area was identified
        if (!droppedPrintAreaCode) {
            return;
        }

        // Stop if the print area was dropped onto itself
        if (droppedPrintAreaCode === targetPrintArea.code) {
            return;
        }

        // Sort the current print areas by their persisted ordering
        const orderedPrintAreas = [...selectedSurfaceData.details.print_areas].sort((firstPrintArea, secondPrintArea) => {
            return Number(firstPrintArea.ordering) - Number(secondPrintArea.ordering);
        });

        // Find the current position of the dragged print area
        const droppedIndex = orderedPrintAreas.findIndex((printArea) => printArea.code === droppedPrintAreaCode);

        // Find the current position of the target print area
        const targetIndex = orderedPrintAreas.findIndex((printArea) => printArea.code === targetPrintArea.code);

        // Stop if either card can no longer be found
        if (droppedIndex < 0 || targetIndex < 0) {
            return;
        }

        // Remove the dragged print area from its old position
        const reorderedPrintAreas = [...orderedPrintAreas];
        const movedPrintAreas = reorderedPrintAreas.splice(droppedIndex, 1);

        // Insert the dragged print area at the target ordering position
        reorderedPrintAreas.splice(targetIndex, 0, movedPrintAreas[0]);

        // Prevent further ordering changes until this sequence has been persisted
        setReorderingPrintAreas(true);

        // Save the complete new ordering
        const response = await reorderPrintAreas(
            selectedCustomProduct.code,
            selectedSurfaceData,
            reorderedPrintAreas,
            setSelectedCustomProduct,
            setSelectedSurfaceData
        );

        // Clear the saving state
        setReorderingPrintAreas(false);

        // Show an error only when the order update failed
        if (!response?.success) {
            toastError({ title: "Error", message: "Could not update print area order: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000 });
        }

    };


    // Clear drag state when dragging finishes
    const handlePrintAreaDragEnd = () => {

        // Clear the dragged print area
        setDraggedPrintAreaCode(null);

        // Clear the current drop target
        setDragOverPrintAreaCode(null);

    };

    // Function to handle clicking 'prferred' icon per postion
    const handleSetPreferredPosition = (fitOrFill, postion) => {

        const newPositionData = fitOrFill === "fill" ? 

        // Update position data so preferred is "Y"
        {

            // Copy all current fields
            ...postion,

            // Override preferred to "Y"
            preferred_fill: "Y"

        } : 
        
            // Update position data so preferred is "Y"
            {

                // Copy all current fields
                ...postion,

                // Override preferred to "Y"
                preferred_fit: "Y"

            };

        // Make the call to update the position
        addUpdatePosition(selectedCustomProduct.code, selectedSurfaceData, selectedPrintAreaData.code, newPositionData, setSelectedCustomProduct, setSelectedSurfaceData, setSelectedPrintAreaData);

    };



    // Function to handle updating a view
    const handleUpdateView = async (view, viewData) => {

        // Make the call
        const response = await updateView(selectedCustomProduct.code, selectedSurfaceData, selectedPrintAreaData.code, view.style, view.name, viewData, setSelectedCustomProduct, setSelectedSurfaceData, setSelectedPrintAreaData);

        // If successful
        if (response?.success) {

            // Close the popup
            setViewPopupIsOpen(false);

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not update view: " + response.error_message, autoCloseMs: 6000 });

        }

        return response;

    };



    // Function to handle toggling a view active state
    const handleToggleViewActive = async (view, event) => {

        // Convert the toggle value to the API flag
        let active = "N";
        if (event.target.value) { active = "Y"; }

        // Build the updated view data
        const viewData = {
            style: view.style,
            name: view.name,
            aspects: view.aspects,
            rotation: view.rotation,
            active: active
        };

        // Make the call
        const response = await updateView(selectedCustomProduct.code, selectedSurfaceData, selectedPrintAreaData.code, view.style, view.name, viewData, setSelectedCustomProduct, setSelectedSurfaceData, setSelectedPrintAreaData);

        // If it failed, show the error
        if (!response?.success) {

            // Toast fail message
            toastError({ title: "Error", message: "Could not update view: " + response.error_message, autoCloseMs: 6000 });

        }

        return response;

    };



    // Function to handle deleting a view
    const handleDeleteView = (view) => {

        // Build the label from the view identity
        const viewLabel = view.style + " / " + view.name;

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this view: " + viewLabel + "?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {
            
                // Return early if they cancel
                if (is_confirmed) {
                    
                    // Make the call
                    const response = await deleteView(selectedCustomProduct.code, selectedSurfaceData, selectedPrintAreaData.code, view.style, view.name, setSelectedCustomProduct, setSelectedSurfaceData, setSelectedPrintAreaData);

                    // If successful
                    if (response?.success) {

                        // Close the popup
                        setViewPopupIsOpen(false);

                    } else {

                        // Toast fail message
                        toastError({ title: "Error", message: "Could not delete view: " + response.error_message, autoCloseMs: 6000 });

                    }
                
                } 

            }
    
        )

    };



    // Tabs for Positions and Views
    const printAreaTabs = [
        { label: 'Views' },
        { label: 'Positions' },
    ];



    // Function to render the tab content on the print area details page
    const renderPrintAreaTabContent = () => {

        switch (activePrintAreaTab) {

            case 0:

                // Views Tab

                return (

                    // Views tab component  
                    // Map each view and include add new option

                    <div className="surfaces-container">

                        {selectedPrintAreaData?.views?.map((view) => {

                            // Build the view label from the view identity
                            const viewLabel = view.style + " / " + view.name;

                            // Build the supported aspect label
                            let viewAspectsLabel = "";
                            if (Array.isArray(view.aspects)) { viewAspectsLabel = view.aspects.join(", "); }

                            // Get the preview asset
                            const preview = view.assets.find((asset) => asset.type === "preview");

                            // Get the background image from the preview asset
                            const backgroundImage = preview.layers.find((layer) => layer.type === "background");

                            // Get the trim image from the preview asset
                            const trimImage = preview.layers.find((layer) => layer.type === "trim");

                            return (
                    
                            <div key={view.style + "::" + view.name} className="surface-container surface-container-with-image" onClick={() => { setSelectedView(view); setViewPopupIsOpen(true) } }>

                                <div 
                                    onClick={(event) => event.stopPropagation()}
                                    className="surface-details__active-toggle"
                                >

                                    <ToggleSwitch
                                        name={"view_active_" + view.style + "_" + view.name}
                                        value={view.active === "Y"}
                                        labelPosition="right"
                                        size="xs"
                                        onChange={(event) => handleToggleViewActive(view, event)}
                                    />

                                </div>

                                <div className="surface-details">
                                    
                                    <div className="surface-details_image-container" title={viewLabel}>
                                            
                                        <ImageLoader src={backgroundImage.image_url} alt="Preview" className="surface-details_image" wrapperCSS={"NA"} imageLoaderImageCSS="NA" />
                                        <ImageLoader src={trimImage.image_url} alt="Trim" className="surface-details_image" wrapperCSS={"NA"}  imageLoaderImageCSS="NA"/>

                                    </div>
                                
                                    <p><span className="surfaces-description">{viewLabel}</span></p>

                                </div> 

                                <div className="surface-container__overlay">

                                    <p><span className="surfaces-description">{viewLabel}</span></p>

                                    <p><span className="surfaces-description">Aspects: {viewAspectsLabel}</span></p>

                                    <p><span className="surfaces-description">Rotation: {view.rotation}</span></p>
                            
                                    <p><span className="surfaces-description">Reference: {view.reference}</span></p>

                                </div>

                            </div>

                        )})}

                        <div className="surface-container__add-new surface-container-with-image">

                            {addingNewCustomProduct ? 
                            
                                <p>You need to save the product before adding views.</p>

                                :

                                <CreateButton
                                    title={
                                        // Show loading text until Customizr is ready.
                                        isCustomizrReady ? "+ Add View" : "Loading Customizr..."
                                    }
                                    size="s"
                                    onClick={() => {
                                        handleOpenCustomizrClick("view");
                                    }}
                                    disabled={
                                        // Block clicks until Customizr is ready.
                                        !isCustomizrReady
                                    }
                                />

                            }
                            
                        </div>
                        
                        {viewPopupIsOpen &&
                        
                            <RenderViewPopup 
                                isOpen={viewPopupIsOpen} 
                                onClose={() => {
                                    setViewPopupIsOpen(false);
                                }}
                                view={selectedView}
                                handleUpdateView={handleUpdateView}
                                handleDeleteView={handleDeleteView}
                            />
                        
                        }

                    </div>

                );

                

            case 1:

                // Positions Tab

                // If there's no views
                if (!selectedPrintAreaData?.views || selectedPrintAreaData?.views.length === 0) {

                    return (

                        <div>There's currently no views for this print area, you need to add a view before you can add a position.</div>

                    )

                } 

                return (

                    // Positions tab component  
                    // Map each position and include add new option

                    <div className="surfaces-container">
                        
                        {selectedPrintAreaData?.relative_positions?.map((position, index) => (
                    
                            <div key={position.code} className="surface-container surface-container-with-text" onClick={() => { setSelectedPosition(position); setAddEditPositionPopupIsOpen(true) }}>

                                <div>

                                    <div 
                                        title={position.preferred_fit === "Y" ? "Preferred Fit Position" : "Set To Preferred Fit Position"}
                                        className="custom-product_preferred-container custom-product_preferred-container--fit"
                                        onClick={(event) => {

                                            // Prevent position click
                                            event.stopPropagation();

                                            // If this is already the preferred postion
                                            if (position.preferred_fit === "Y") {

                                                // return early
                                                return;

                                            } else {

                                                // Set as the preferred position
                                                handleSetPreferredPosition("fit", position)

                                            }
                                        }}     
                                    >
                                        <span>Fit {position.preferred_fit === "Y" ? "⭐" : "★"}</span>
                                    </div>

                                    <div 
                                        title={position.preferred_fill === "Y" ? "Preferred Fill Position" : "Set To Preferred Fill Position"}
                                        className="custom-product_preferred-container custom-product_preferred-container--fill"
                                        onClick={(event) => {

                                            // Prevent position click
                                            event.stopPropagation();

                                            // If this is already the preferred postion
                                            if (position.preferred_fill === "Y") {

                                                // return early
                                                return;

                                            } else {

                                                // Set as the preferred position
                                                handleSetPreferredPosition("fill", position)

                                            }
                                        }}     
                                    >
                                        <span>Fill {position.preferred_fill === "Y" ? "⭐" : "★"}</span>
                                    </div>

                                </div>

                                <div className="surface-details">

                                    <p><span className="surfaces-title">{position.name}</span></p>

                                    <p>{position.code}</p>

                                </div>
                                
                            </div>

                        ))}

                        <div className="surface-container__add-new surface-container-with-text">

                            {addingNewCustomProduct ? 
                            
                                <p>You need to save the product before adding positions.</p>

                                :

                                <>
                                {/* <CreateButton title="+ Add Position" size="s" onClick={() => { setSelectedPosition(null); setAddEditPositionPopupIsOpen(true) }} /> */}

                                <CreateButton title="+ Add Position" size="s" onClick={() => { setSelectedPosition(null); handleOpenCustomizrClick("position") }} />

                                </>

                            }
                            
                        </div>
                        
                        {
                        
                        addEditPositionPopupIsOpen &&
                        
                            <RenderPositionDetails 
                                isOpen={addEditPositionPopupIsOpen} 
                                onClose={() => {
                                    setAddEditPositionPopupIsOpen(false);
                                }}
                                positionData={selectedPosition}
                                setSelectedPosition={setSelectedPosition}

                                selectedPrintAreaData={selectedPrintAreaData}
                                setSelectedPrintAreaData={setSelectedPrintAreaData}

                                selectedSurfaceData={selectedSurfaceData}
                                setSelectedSurfaceData={setSelectedSurfaceData}

                                selectedCustomProduct={selectedCustomProduct}
                                setSelectedCustomProduct={setSelectedCustomProduct}

                                handleOpenCustomizrClick={handleOpenCustomizrClick}

                            />
                        
                        }

                    </div>

                );
            
            default:

                return null;

        }

    };



    // **** Opening Customizr **** //

    // Click handler to open customizr
    const handleOpenCustomizrClick = async (tokenType, itemId = null) => {

        try {

            // Build the Customizr configuration
            const customizrConfig = {
                type: tokenType,
                productCode: selectedCustomProduct?.code,
                surfaceCode: selectedSurfaceData?.details?.code,
                printAreaCode: selectedPrintAreaData?.code,
            };

            // Pass the existing mockup id when editing a mockup
            if (tokenType === "mockup" && itemId) {
                customizrConfig.mockup_id = itemId;
            }

            // Pass the existing print layout id when editing a print layout
            if (tokenType === "printlayout" && itemId) {
                customizrConfig.print_layout_id = itemId;
            }

            // Open Customizr and listen for any message it sends back
            await openCustomizr(customizrConfig, () => {

                // Reload the product data when Customizr says something changed
                refreshCustomProductSelections(tokenType);

            });

        } catch (error) {

            // Handle errors or user cancellation
            console.error("Customizr did not complete:", error);
            toastError({ title: "Error", message: error?.message || "Something went wrong while opening Customizr." });

        }

    };











    // **** Custom Product Details **** //


    // Function to handle changing the image in custom product details
    const handleImageReset = () => {

        // Empty the image data from the formValues - Manually call handleChange with a fake event object
        handleChange({

            // Provide a target object that has name and value
            target: {

                name: "custom_product_thumbnail_url",

                // Set it empty so the RenderImageUploader shows
                value: "",

            },

        });

    };

    // Function to handle cloning a mockup
    const handleCloneMockup = async (mockup, targetProductCode, allChildren = "N") => {

        const response = await cloneMockup(selectedCustomProduct.code, mockup.id, targetProductCode, allChildren);

        if (!response?.success) {
            toastError({ title: "Error", message: "Could not clone mockup: " + response.error_message, autoCloseMs: 6000 });
        }

        return response;

    };

    // Function to handle updating a mockup
    const handleUpdateMockup = async (mockup, viewSettings) => {

        // Build the editable mockup data
        const mockupData = {
            view_settings: viewSettings
        };

        // Make the call
        const response = await updateMockup(selectedCustomProduct.code, mockup.id, mockupData, setSelectedCustomProduct);

        // If successful
        if (response?.success) {

            // Close the popup
            setMockupPopupIsOpen(false);

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not update mockup: " + response.error_message, autoCloseMs: 6000 });

        }

        return response;

    };



    // Function to handle deleting a mockup
    const handleDeleteMockup = (mockup) => {

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this mockup?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {
            
                // Return early if they cancel
                if (is_confirmed) {
                    
                    // Make the call
                    const response = await deleteMockup(selectedCustomProduct.code, mockup.id, setSelectedCustomProduct);

                    // If successful
                    if (response.success) {

                        // Close the popup
                        setMockupPopupIsOpen(false);

                    } else {

                        // Toast fail message
                        toastError({ title: "Error", message: "Could not delete mockup: " + response.error_message, autoCloseMs: 6000 });

                    }

                };

            }
            
        );

    };



    // Function to handle deleting a print layout
    const handleDeletePrintLayout = (printLayout) => {

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this print layout?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {
            
                // Return early if they cancel
                if (is_confirmed) {
                    
                    // Make the call
                    const response = await deletePrintLayout(selectedCustomProduct.code, printLayout.id, setSelectedCustomProduct);

                    // If successful
                    if (response.success) {

                        // Close the popup
                        setPrintLayoutPopupIsOpen(false);

                    } else {

                        // Toast fail message
                        toastError({ title: "Error", message: "Could not delete print layout: " + response.error_message, autoCloseMs: 6000 });

                    }

                };

            }
            
        );

    };



    // Render the content selected in the editor tree
    const renderEditorContent = () => {

        switch (activeSection) {

            case "surfaces":

                // A selected print area is the deepest node in the Surfaces branch
                if (selectedPrintAreaData) {

                    return (
                        <RenderPrintAreaDetails
                            selectedPrintAreaData={selectedPrintAreaData}
                            updateExistingPrintArea={updateExistingPrintArea}
                            selectedCustomProduct={selectedCustomProduct}
                            setSelectedCustomProduct={setSelectedCustomProduct}
                            setSelectedPrintAreaData={setSelectedPrintAreaData}
                            printAreaTabs={printAreaTabs}
                            renderPrintAreaTabContent={renderPrintAreaTabContent}
                            activePrintAreaTab={activePrintAreaTab}
                            setActivePrintAreaTab={setActivePrintAreaTab}
                            selectedSurfaceData={selectedSurfaceData}
                            setSelectedSurfaceData={setSelectedSurfaceData}
                            customProductsMMMLinks={customProductsMMMLinks}
                            printAreaRoles={printAreaRoles}
                            setPrintAreaRoles={setPrintAreaRoles}
                            onDirtyChange={setNestedEditorDirty}
                        />
                    );

                }

                // A selected surface shows its details and its print areas together
                if (selectedSurfaceData) {

                    return (
                        <RenderSurfaceDetails
                            selectedSurfaceData={selectedSurfaceData}
                            updateExistingSurface={updateExistingSurface}
                            selectedCustomProduct={selectedCustomProduct}
                            setSelectedCustomProduct={setSelectedCustomProduct}
                            setSelectedSurfaceData={setSelectedSurfaceData}
                            renderSurfacePrintAreas={renderSurfacePrintAreas}
                            onDirtyChange={setNestedEditorDirty}
                        />
                    );

                }

                // Surfaces root - show all surfaces and the add action
                return (

                    <div className="custom-product-editor__panel">

                        <div className="custom-product-editor__section-heading">
                            <h3>Surfaces</h3>
                        </div>

                        <div className="surfaces-container">

                            {selectedCustomProduct?.surfaces?.map((surface, index) => (

                                <div
                                    key={surface.code}
                                    className="surface-container surface-container-with-text"
                                    onClick={() => handleEditorNavigation("surfaces", {details: surface, index: index})}
                                >

                                    <div className="surface-details">
                                        <p><span className="surfaces-title">{surface.name}</span></p>
                                        <p>{surface.code}</p>
                                        <p><span className="surfaces-description">{surface.description}</span></p>
                                    </div>

                                </div>

                            ))}

                            <div className="surface-container__add-new surface-container-with-text">

                                {addingNewCustomProduct

                                    ? <p>You need to save the product before adding surfaces.</p>

                                    : <CreateButton title="+ Add Surface" size="s" onClick={() => {setAddNewSurfacePopupIsOpen(true)}} />
                                }

                            </div>

                        </div>

                        {addNewSurfacePopupIsOpen &&

                            <RenderAddNewSurfacePopup
                                isOpen={addNewSurfacePopupIsOpen}
                                onClose={() => {
                                    setAddNewSurfacePopupIsOpen(false);
                                }}
                                handleSaveNewSurface={handleSaveNewSurface}
                            />

                        }

                    </div>

                );


            case "colourways":

                return (

                    <div className="custom-product-editor__panel">

                        <div className="custom-product-editor__section-heading">
                            <h3>Colourways</h3>
                        </div>

                        <RenderColourwayDetails
                            selectedCustomProduct={selectedCustomProduct}
                            setSelectedCustomProduct={setSelectedCustomProduct}
                            addingNewCustomProduct={addingNewCustomProduct}
                            addNewCustomProductColourway={addNewCustomProductColourway}
                            updateExistingColourway={updateExistingColourway}
                            deleteColourway={deleteColourway}
                        />

                    </div>

                );


            case "mockups":

                return (

                    <div className="custom-product-editor__panel">

                        <div className="custom-product-editor__section-heading">
                            <h3>Mockups</h3>
                        </div>

                        <div className="surfaces-container">

                            {selectedCustomProduct?.mockups?.map((mockup, index) => {

                                return (

                                    <div key={index} className="surface-container surface-container-with-image" onClick={() => { setSelectedMockup(mockup); setMockupPopupIsOpen(true) } }>

                                        <div className="surface-details">

                                            <div className="surface-details_image-container">
                                                <ImageLoader src={mockup.mockup_thumbnail_url} alt="Mockup" className="surface-details_image" wrapperCSS={"NA"} imageLoaderImageCSS="NA" />
                                            </div>

                                            <div>{mockup.id}</div>

                                        </div>

                                    </div>

                                );

                            })}

                            <div className="surface-container__add-new surface-container-with-image">

                                {addingNewCustomProduct

                                    ? <p>You need to save the product before adding mockups.</p>

                                    :

                                        <CreateButton
                                            title={isCustomizrReady ? "+ Add Mockup" : "Loading Customizr..."}
                                            size="s"
                                            onClick={() => {
                                                handleOpenCustomizrClick("mockup");
                                            }}
                                            disabled={!isCustomizrReady}
                                        />
                                }

                            </div>

                        </div>

                        {mockupPopupIsOpen &&

                            <RenderMockupPopup
                                isOpen={mockupPopupIsOpen}
                                onClose={() => {
                                    setMockupPopupIsOpen(false);
                                }}
                                mockup={selectedMockup}
                                productCode={selectedCustomProduct.code}
                                getMockupCloneTargets={getMockupCloneTargets}
                                handleCloneMockup={handleCloneMockup}
                                handleUpdateMockup={handleUpdateMockup}
                                handleDeleteMockup={handleDeleteMockup}
                                handleOpenCustomizrClick={handleOpenCustomizrClick}
                                isCustomizrReady={isCustomizrReady}
                            />

                        }

                    </div>

                );


            case "print-layouts":

                return (

                    <div className="custom-product-editor__panel">

                        <div className="custom-product-editor__section-heading">
                            <h3>Print Layouts</h3>
                        </div>

                        <div className="surfaces-container">

                            {selectedCustomProduct?.print_layouts?.map((printLayout, index) => {

                                return (

                                    <div key={index} className="surface-container surface-container-with-text" onClick={() => { setSelectedPrintLayout(printLayout); setPrintLayoutPopupIsOpen(true) } }>

                                        <div className="surface-details">
                                            <p><span className="surfaces-title">{printLayout.ordering}</span></p>
                                            <p><span className="surfaces-title">{printLayout.production_title}</span></p>
                                            <p>{printLayout.code}</p>
                                            <p><span className="surfaces-description">{printLayout.description}</span></p>
                                        </div>

                                    </div>

                                );

                            })}

                            <div className="surface-container__add-new surface-container-with-text">

                                {addingNewCustomProduct

                                    ? <p>You need to save the product before adding print layouts.</p>

                                    : selectedCustomProduct?.mockups.length === 0

                                        ? <p>You need to make a mock up before making a print area, not technically essential but highly recommended.</p>

                                        :

                                            <CreateButton
                                                title={isCustomizrReady ? "+ Add Print Layout" : "Loading Customizr..."}
                                                size="s"
                                                onClick={() => {
                                                    handleOpenCustomizrClick("printlayout");
                                                }}
                                                disabled={!isCustomizrReady}
                                            />
                                }

                            </div>

                        </div>

                        {printLayoutPopupIsOpen &&

                            <RenderPrintLayoutPopup
                                isOpen={printLayoutPopupIsOpen}
                                onClose={() => {
                                    setPrintLayoutPopupIsOpen(false);
                                }}
                                printLayoutData={selectedPrintLayout}
                                handleDeletePrintLayout={handleDeletePrintLayout}
                                selectedCustomProduct={selectedCustomProduct}
                                setSelectedCustomProduct={setSelectedCustomProduct}
                                setSelectedPrintLayout={setSelectedPrintLayout}
                                handleOpenCustomizrClick={handleOpenCustomizrClick}
                                isCustomizrReady={isCustomizrReady}
                            />

                        }

                    </div>

                );


            default:

                return null;

        }

    };


    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--large custom-product-editor-popup">

                {/* Standard large popup header */}
                <div className="popup__header">

                    <div className="popup__headerLeft">

                        <h2 className="popup__title">
                            {addingNewCustomProduct ? "New Custom Product" : (formValues.name || selectedCustomProduct?.name || "Custom Product")}
                        </h2>

                        <p className="popup__subtitle">
                            {addingNewCustomProduct ? "Create a custom product" : (formValues.code || selectedCustomProduct?.code || "")}
                        </p>

                    </div>

                    <div className="popup__headerRight">

                        <button
                            type="button"
                            className="popup__close"
                            onClick={() => handleClose()}
                            disabled={loading}
                            title="Close"
                        >
                            ×
                        </button>

                    </div>

                </div>


                <div className="custom-product-editor__body">

                    {/* Persistent tree navigation */}
                    <nav className="custom-product-editor__navigation">

                        <button
                            type="button"
                            className={`custom-product-editor__navigation-item ${activeSection === "product" ? "custom-product-editor__navigation-item--active" : ""}`}
                            onClick={() => handleEditorNavigation("product")}
                        >
                            Product
                        </button>

                        <button
                            type="button"
                            className={`custom-product-editor__navigation-item ${activeSection === "surfaces" && !selectedSurfaceData ? "custom-product-editor__navigation-item--active" : ""}`}
                            onClick={() => handleEditorNavigation("surfaces")}
                        >
                            Surfaces
                        </button>

                        {/* Surface and print area records are children of the Surfaces branch */}
                        {(selectedCustomProduct?.surfaces || []).map((surface, surfaceIndex) => {

                            const surfaceData = {details: surface, index: surfaceIndex};

                            return (

                                <React.Fragment key={surface.code}>

                                    <button
                                        type="button"
                                        className={`custom-product-editor__navigation-item custom-product-editor__navigation-item--level-1 ${activeSection === "surfaces" && selectedSurfaceData?.details?.code === surface.code && !selectedPrintAreaData ? "custom-product-editor__navigation-item--active" : ""}`}
                                        onClick={() => handleEditorNavigation("surfaces", surfaceData)}
                                    >
                                        {surface.name || surface.code}
                                    </button>

                                    {[...(surface.print_areas || [])]
                                        .sort((firstPrintArea, secondPrintArea) => Number(firstPrintArea.ordering) - Number(secondPrintArea.ordering))
                                        .map((printArea) => (

                                            <button
                                                key={surface.code + "::" + printArea.code}
                                                type="button"
                                                className={`custom-product-editor__navigation-item custom-product-editor__navigation-item--level-2 ${activeSection === "surfaces" && selectedSurfaceData?.details?.code === surface.code && selectedPrintAreaData?.code === printArea.code ? "custom-product-editor__navigation-item--active" : ""}`}
                                                onClick={() => handleEditorNavigation("surfaces", surfaceData, printArea)}
                                            >
                                                {printArea.name || printArea.code}
                                            </button>

                                        ))}

                                </React.Fragment>

                            );

                        })}

                        <button
                            type="button"
                            className={`custom-product-editor__navigation-item custom-product-editor__navigation-item--section-gap ${activeSection === "colourways" ? "custom-product-editor__navigation-item--active" : ""}`}
                            onClick={() => handleEditorNavigation("colourways")}
                        >
                            Colourways
                        </button>

                        <button
                            type="button"
                            className={`custom-product-editor__navigation-item ${activeSection === "mockups" ? "custom-product-editor__navigation-item--active" : ""}`}
                            onClick={() => handleEditorNavigation("mockups")}
                        >
                            Mockups
                        </button>

                        <button
                            type="button"
                            className={`custom-product-editor__navigation-item ${activeSection === "print-layouts" ? "custom-product-editor__navigation-item--active" : ""}`}
                            onClick={() => handleEditorNavigation("print-layouts")}
                        >
                            Print Layouts
                        </button>

                    </nav>


                    {/* Editor for the selected tree node */}
                    <div className="popup-content popup-content__body custom-product-editor__content">

                        {activeSection === "product"

                            ?

                                <div className="custom-product-editor__panel">

                                    <div className="custom-product-editor__section-heading">
                                        <h3>Product</h3>
                                    </div>

                                    <div className="custom-product-details-conatiner">

                                        <div className="custom-product-details-conatiner__left">

                                            <div>

                                                {/* Display / Upload image */}
                                                {imageDetails.image_url

                                                    ?

                                                        <div className="custom-product-details__image-container">

                                                            <ImageLoader src={imageDetails.image_url} alt={imageDetails.name} className="preview-pane__image" />

                                                            <div className="custom-product-details__image-container__reset">

                                                                <CreateButton
                                                                    tooltip="Reload Image"
                                                                    title="⭯"
                                                                    size="xs"
                                                                    onClick={() => {
                                                                        setImageDetails([]);
                                                                        handleImageReset();
                                                                    }}
                                                                />

                                                            </div>

                                                        </div>

                                                    : formValues.custom_product_thumbnail_url

                                                        ?

                                                            <div className="custom-product-details__image-container">

                                                                <ImageLoader src={formValues.custom_product_thumbnail_url} alt={formValues.name} className="preview-pane__image" />

                                                                <div className="custom-product-details__image-container__reset">
                                                                    <CreateButton tooltip="Reload Image" title="⭯" size="xs" onClick={() => handleImageReset()} />
                                                                </div>

                                                            </div>

                                                        :

                                                            <RenderImageUploader
                                                                label="page-image"
                                                                selectedImage={formValues.custom_product_thumbnail_url || null}
                                                                onImageChange={(payload) => {

                                                                    // Store either the bucket id or the uploaded image data for the save call
                                                                    handleChange({
                                                                        target: {
                                                                            name: "image_data",
                                                                            value: payload.imageBucketId ? payload.imageBucketId : payload.uploadedImage,
                                                                        },
                                                                    });

                                                                    // Display the selected image immediately
                                                                    handleChange({
                                                                        target: {
                                                                            name: "custom_product_thumbnail_url",
                                                                            value: payload.imageDetails.image_url,
                                                                        },
                                                                    });

                                                                }}
                                                                addToImageBucket={false}
                                                            />

                                                }

                                            </div>

                                        </div>

                                        <div className="custom-product-details-conatiner__middle">

                                            <TwoColumnList
                                                property={<label>Name</label>}
                                                value={<input name="name" type="text" value={formValues.name || ""} onChange={handleChange} placeholder="Name" required />}
                                            />

                                            <TwoColumnList
                                                property={<label>Code</label>}
                                                value={<input name="code" type="text" value={formValues.code || ""} onChange={handleChange} placeholder="Code" required />}
                                            />

                                            <TwoColumnList
                                                property={<label>Stock Code</label>}
                                                value={<input name="stock_code" type="text" value={formValues.stock_code || ""} onChange={handleChange} placeholder="Stock Code" />}
                                            />

                                            <TwoColumnList
                                                property={<label>Stock Used</label>}
                                                value={<input name="stock_used" type="number" min="0" value={formValues.stock_used || ""} onChange={handleChange} placeholder="1" />}
                                            />

                                            <TwoColumnList
                                                property={<label>Brand</label>}
                                                value={<input name="brand" type="text" value={formValues.brand || ""} onChange={handleChange} placeholder="Brand" />}
                                            />

                                            <TwoColumnList
                                                property={<label>Description</label>}
                                                value={<textarea name="description" rows="3" cols="30" value={formValues.description || ""} onChange={handleChange} placeholder="Description"></textarea>}
                                            />

                                            <TwoColumnList
                                                property={<label>Category</label>}
                                                value={<input name="category" type="text" value={formValues.category || ""} onChange={handleChange} placeholder="Category" />}
                                            />

                                            <TwoColumnList
                                                property={<label>Sub Category</label>}
                                                value={<input name="sub_category" type="text" value={formValues.sub_category || ""} onChange={handleChange} placeholder="Sub Category" />}
                                            />

                                            <TwoColumnList
                                                property={<label>Product Price</label>}
                                                value={<input name="nominal_product_price" type="number" min="0" step="any" value={formValues.nominal_product_price || ""} onChange={handleChange} placeholder="Product Price" />}
                                            />

                                            <TwoColumnList
                                                property={
                                                    <>
                                                        <label>Product Profit</label>

                                                        <InfoButton
                                                            shortLabel="Product Profit"
                                                            description="A minimim profit amount for the product. This is used to calculate the RRP based on the configuration of the product and it's print areas."
                                                        />
                                                    </>
                                                }
                                                value={
                                                    <input
                                                        name="nominal_product_profit"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={formValues.nominal_product_profit === null || formValues.nominal_product_profit === undefined ? "" : formValues.nominal_product_profit}
                                                        onChange={(event) => {
                                                            handleChange({
                                                                target: {
                                                                    name: "nominal_product_profit",
                                                                    value: event.target.value === "" ? null : event.target.value
                                                                }
                                                            });
                                                        }}
                                                        placeholder="Product Profit"
                                                    />
                                                }
                                            />

                                        </div>

                                    </div>


                                    {/* Product-level save action */}
                                    <div className="custom-product-editor__section-actions horizontal-align-content-center flex-justify-left-right">

                                        <div></div>

                                        {userData.email !== "customizr@strichsuite.com" &&

                                            <div className="warning-message" style={{fontWeight: "bold", fontSize: "larger"}}>
                                                This is not a Strich account so you can't edit anything!
                                            </div>

                                        }

                                        <div>

                                            {(addingNewCustomProduct || isFormPartlyEdited()) &&

                                                <CreateButton
                                                    title={addingNewCustomProduct ? "Create Custom Product" : "Save changes"}
                                                    size="m"
                                                    onClick={() => handleSave()}
                                                    disabled={loading}
                                                />

                                            }

                                        </div>

                                    </div>

                                </div>

                            : renderEditorContent()

                        }

                    </div>

                </div>


                {/* Product deletion belongs to the popup itself rather than the currently selected section */}
                <div className="tabs-footer tabs-footer--no-background horizontal-align-content-center flex-justify-left-right custom-product-editor__popup-footer">

                    <div>

                        {!addingNewCustomProduct &&

                            <CreateButton
                                title="Delete Custom Product"
                                isDelete={true}
                                tooltip="Delete Custom Product"
                                size="m"
                                icon="/images/icon_bin.png"
                                onClick={() => handleDelete(selectedCustomProduct.code)}
                            />

                        }

                    </div>

                    <CreateButton
                        title="Close"
                        invert
                        size="m"
                        onClick={() => handleClose()}
                        disabled={loading}
                    />

                </div>


                {loading &&
                    <LoadingScreen loadingMessage="Updating..." />
                }

            </div>

        </div>

    );

}; export default CustomProduct;
